/* eslint-disable default-case */
import produce from 'immer';

import {
  types,
  ClientIntegration,
  ClientIntegrationsState,
  ClientIntegrationsActionTypes,
  CreateClientIntegrationPayload,
  GetClientIntegrationsPending,
  GetClientIntegrationsSuccess,
  GetClientIntegrationsError,
  CreateClientIntegrationPending,
  CreateClientIntegrationError,
  UpdateClientIntegrationPayload,
  UpdateClientIntegrationPending,
  UpdateClientIntegrationSuccess,
  UpdateClientIntegrationError,
  DeleteClientIntegrationPending,
  DeleteClientIntegrationError,
  GetTicketingEventsPending,
  GetTicketingEventsSuccess,
  GetTicketingEventsError,
  CreateCustomerRequest,
  CreateCustomerPending,
  CreateCustomerSuccess,
  CreateCustomerError,
  TestAccessRequest,
  TestAccessPending,
  TestAccessError,
  GetPaymentSettingsSuccess,
  UpdatePaymentSettingsPayload,
  UpdatePaymentSettingsPending,
  UpdatePaymentSettingsError,
  GetProductsPending,
  GetProductsSuccess,
  GetProductsError,
  SyncProductsPending,
  SyncProductsSuccess,
  SyncProductsError,
  SyncProductsPayload
} from './types';

// Actions
export const actions = {
  getClientIntegrations: (): ClientIntegrationsActionTypes => ({ type: types.GET_CLIENT_INTEGRATIONS.REQUEST }),
  createClientIntegration: (payload: CreateClientIntegrationPayload): ClientIntegrationsActionTypes => ({
    type: types.CREATE_CLIENT_INTEGRATION.REQUEST,
    payload
  }),
  createCustomer: (payload: CreateCustomerRequest['payload']): ClientIntegrationsActionTypes => ({
    type: types.CREATE_CUSTOMER.REQUEST,
    payload
  }),
  testAPIAccess: (payload: TestAccessRequest['payload']): ClientIntegrationsActionTypes => ({
    type: types.TEST_ACCESS.REQUEST,
    payload
  }),
  updateClientIntegration: (payload: UpdateClientIntegrationPayload): ClientIntegrationsActionTypes => ({
    type: types.UPDATE_CLIENT_INTEGRATION.REQUEST,
    payload
  }),
  deleteClientIntegration: (payload: { id: string }): ClientIntegrationsActionTypes => ({
    type: types.DELETE_CLIENT_INTEGRATION.REQUEST,
    payload
  }),
  getTicketingEvents: (payload: string): ClientIntegrationsActionTypes => ({
    type: types.GET_TICKETING_EVENTS.REQUEST,
    payload
  }),
  getPaymentSettings: (payload: string): ClientIntegrationsActionTypes => ({
    type: types.GET_PAYMENT_SETTINGS.REQUEST,
    payload
  }),
  updatePaymentSettings: (payload: UpdatePaymentSettingsPayload): ClientIntegrationsActionTypes => ({
    type: types.UPDATE_PAYMENT_SETTINGS.REQUEST,
    payload
  }),
  getProducts: (payload: string): ClientIntegrationsActionTypes => ({
    type: types.GET_PRODUCTS.REQUEST,
    payload
  }),
  syncProducts: (payload: SyncProductsPayload): ClientIntegrationsActionTypes => ({
    type: types.SYNC_PRODUCTS.REQUEST,
    payload
  }),
  resetEvents: (): ClientIntegrationsActionTypes => ({ type: types.RESET_EVENTS }),
  resetCustomer: (): ClientIntegrationsActionTypes => ({ type: types.RESET_CUSTOMER }),
  resetAPIAccess: (): ClientIntegrationsActionTypes => ({ type: types.RESET_API_ACCESS }),
  resetError: (): ClientIntegrationsActionTypes => ({ type: types.RESET_ERROR }),
  resetAction: (): ClientIntegrationsActionTypes => ({ type: types.RESET_ACTION })
};

// Initial State
export const initialState: ClientIntegrationsState = {
  events: [],
  clientIntegrations: [],
  customer: null,
  paymentSettings: null,
  products: null,
  hasTransactions: false,
  apiAccess: false,
  action: {
    pending: false,
    success: false,
    error: null
  }
};

// Reducer
export const reducer = (
  state: ClientIntegrationsState = initialState,
  action: ClientIntegrationsActionTypes
): ClientIntegrationsState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case types.GET_CLIENT_INTEGRATIONS.PENDING:
      case types.CREATE_CLIENT_INTEGRATION.PENDING:
      case types.UPDATE_CLIENT_INTEGRATION.PENDING:
      case types.DELETE_CLIENT_INTEGRATION.PENDING:
      case types.GET_TICKETING_EVENTS.PENDING:
      case types.CREATE_CUSTOMER.PENDING:
      case types.UPDATE_PAYMENT_SETTINGS.PENDING:
      case types.GET_PRODUCTS.PENDING:
      case types.SYNC_PRODUCTS.PENDING:
      case types.TEST_ACCESS.PENDING:
        draft.action.pending = (
          action as
            | GetClientIntegrationsPending
            | CreateClientIntegrationPending
            | UpdateClientIntegrationPending
            | DeleteClientIntegrationPending
            | GetTicketingEventsPending
            | CreateCustomerPending
            | TestAccessPending
            | UpdatePaymentSettingsPending
            | GetProductsPending
            | SyncProductsPending
        ).payload;
        break;
      case types.GET_CLIENT_INTEGRATIONS.SUCCESS:
        draft.clientIntegrations = (action as GetClientIntegrationsSuccess).payload;
        break;
      case types.CREATE_CLIENT_INTEGRATION.SUCCESS:
      case types.DELETE_CLIENT_INTEGRATION.SUCCESS:
        draft.action.success = true;
        break;
      case types.UPDATE_CLIENT_INTEGRATION.SUCCESS:
        const payload = (action as UpdateClientIntegrationSuccess).payload;
        if (draft.clientIntegrations.length) {
          const clientIntegrationIndex = draft.clientIntegrations.findIndex(
            (ci) => ci.id === (payload as ClientIntegration).id
          );
          if (clientIntegrationIndex > -1) {
            Object.assign(draft.clientIntegrations[clientIntegrationIndex], payload);
          }
        }
        break;
      case types.GET_TICKETING_EVENTS.SUCCESS:
        draft.events = (action as GetTicketingEventsSuccess).payload;
        draft.action.error = initialState.action.error;
        break;
      case types.CREATE_CUSTOMER.SUCCESS:
        draft.customer = (action as CreateCustomerSuccess).payload;
        break;
      case types.GET_PAYMENT_SETTINGS.SUCCESS:
        const { paymentSettings, hasTransactions } = (action as GetPaymentSettingsSuccess).payload;
        draft.paymentSettings = paymentSettings;
        draft.hasTransactions = hasTransactions;
        break;
      case types.GET_PRODUCTS.SUCCESS:
        draft.products = (action as GetProductsSuccess).payload;
        draft.action.error = initialState.action.error;
        break;
      case types.SYNC_PRODUCTS.SUCCESS:
        draft.products = (action as SyncProductsSuccess).payload;
        draft.action.success = true;
        break;
      case types.TEST_ACCESS.SUCCESS:
        draft.apiAccess = true;
        break;
      case types.GET_CLIENT_INTEGRATIONS.ERROR:
      case types.CREATE_CLIENT_INTEGRATION.ERROR:
      case types.UPDATE_CLIENT_INTEGRATION.ERROR:
      case types.DELETE_CLIENT_INTEGRATION.ERROR:
      case types.CREATE_CUSTOMER.ERROR:
      case types.GET_PAYMENT_SETTINGS.ERROR:
      case types.TEST_ACCESS.ERROR:
      case types.UPDATE_PAYMENT_SETTINGS.ERROR:
      case types.GET_PRODUCTS.ERROR:
      case types.SYNC_PRODUCTS.ERROR:
        draft.action.error = (
          action as
            | GetClientIntegrationsError
            | CreateClientIntegrationError
            | UpdateClientIntegrationError
            | DeleteClientIntegrationError
            | CreateCustomerError
            | TestAccessError
            | UpdatePaymentSettingsError
            | GetProductsError
            | SyncProductsError
        ).payload;
        break;
      case types.UPDATE_PAYMENT_SETTINGS.SUCCESS:
        draft.action.success = true;
        break;
      case types.GET_TICKETING_EVENTS.ERROR:
        draft.action.error = (action as GetTicketingEventsError).payload;
        draft.events = initialState.events;
        break;
      case types.RESET_EVENTS:
        draft.events = initialState.events;
        break;
      case types.RESET_CUSTOMER:
        draft.customer = initialState.customer;
        break;
      case types.RESET_API_ACCESS:
        draft.apiAccess = initialState.apiAccess;
        break;
      case types.RESET_ACTION:
        draft.action = initialState.action;
        break;
      case types.RESET_ERROR:
        draft.action.error = initialState.action.error;
        break;
    }
  });
};
